'use client';

import { useEffect, useMemo, useState } from 'react';

import { Clock, Loader2, Search, Shield, Sparkles } from 'lucide-react';

import { usePredefinedFactors } from '../api/use-predefined-factors';
import type { PredefinedFactorEntry } from '../types';
import type { MappingMemorySuggestion } from '../types/mapping-memory';
import type { FactorSourceChoice } from './FactorSourceSelector';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';

export type FactorSelectorSegment = 'predefined' | 'last-used' | 'ai-assignment' | 'manual-search';

export interface PredefinedFactor {
  id: string;
  name: string;
  scope: number;
  source: string;
  region: string;
  year: number;
  co2ePerUnit: number;
  co2eUnit: string;
  /** Climatiq activity_id if mapped — enables real Climatiq resolve */
  climatiqActivityId?: string | null;
  /** Unit type (Energy, Weight, Volume, Distance, etc.) */
  unitType?: string;
  /** Raw unit string (kWh, kg, l, etc.) */
  unit?: string;
}

interface FactorSegmentedSelectorProps {
  activeSegment: FactorSelectorSegment;
  onSegmentChange: (segment: FactorSelectorSegment) => void;
  lastUsedFactors: MappingMemorySuggestion[];
  onSelectLastUsed: (item: MappingMemorySuggestion) => void;
  predefinedFactors?: PredefinedFactor[];
  onSelectPredefined?: (factor: PredefinedFactor) => void;
  aiAssignmentContent: React.ReactNode;
  manualSearchContent: React.ReactNode;
  /** Scope context from the form (1, 2, 3) for filtering predefined factors */
  scope?: number;
  /** Activity category context (e.g., "stationary_combustion") */
  activityCategory?: string;
  /** Controls which tabs are visible */
  factorSourceChoice?: FactorSourceChoice;
  /** Unit type filter for predefined factors (Energy, Weight, Volume, etc.) */
  unitType?: string;
}

const SEGMENTS: Array<{
  value: FactorSelectorSegment;
  icon: typeof Clock;
  label: string;
}> = [
  { value: 'last-used', icon: Clock, label: 'Last Used' },
  { value: 'predefined', icon: Shield, label: 'Predefined' },
  { value: 'ai-assignment', icon: Sparkles, label: 'AI Assignment' },
  { value: 'manual-search', icon: Search, label: 'Manual Search' },
];

export function FactorSegmentedSelector({
  activeSegment,
  onSegmentChange,
  lastUsedFactors,
  onSelectLastUsed,
  predefinedFactors = [],
  onSelectPredefined,
  aiAssignmentContent,
  manualSearchContent,
  scope,
  activityCategory,
  factorSourceChoice = 'mixed',
  unitType,
}: FactorSegmentedSelectorProps) {
  const { t } = useTranslation('ghg');

  const visibleSegments = useMemo(
    () =>
      SEGMENTS.filter((seg) => {
        if (factorSourceChoice === 'predefined') {
          return seg.value === 'last-used' || seg.value === 'predefined';
        }
        if (factorSourceChoice === 'climatiq') {
          return seg.value !== 'predefined';
        }
        return true;
      }),
    [factorSourceChoice]
  );

  useEffect(() => {
    const visibleValues = new Set(visibleSegments.map((s) => s.value));
    if (!visibleValues.has(activeSegment)) {
      onSegmentChange(visibleSegments[0]?.value ?? 'last-used');
    }
  }, [factorSourceChoice, activeSegment, onSegmentChange, visibleSegments]);

  return (
    <div className="space-y-3">
      <div className="flex gap-0.5 rounded-lg border p-0.5">
        {visibleSegments.map((seg) => {
          const Icon = seg.icon;
          return (
            <Button
              key={seg.value}
              type="button"
              variant={activeSegment === seg.value ? 'default' : 'ghost'}
              size="sm"
              className="flex-1 text-xs"
              onClick={() => { onSegmentChange(seg.value); }}
            >
              <Icon className="mr-1.5 h-3.5 w-3.5" />
              {seg.label}
              {seg.value === 'last-used' && lastUsedFactors.length > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">
                  {String(lastUsedFactors.length)}
                </Badge>
              )}
            </Button>
          );
        })}
      </div>

      {activeSegment === 'predefined' && (
        <PredefinedFactorPanel scope={scope} category={activityCategory} unitType={unitType} onSelectFactor={(factor) => {
          onSelectPredefined?.({
            id: factor.id,
            name: factor.name,
            scope: factor.scope,
            source: factor.climatiqSource ?? 'DEFRA',
            region: factor.region,
            year: 2025,
            co2ePerUnit: factor.referenceValue ? parseFloat(factor.referenceValue) : 0,
            co2eUnit: `kg/${factor.unit}`,
            climatiqActivityId: factor.climatiqActivityId,
            unitType: factor.unitType ?? undefined,
            unit: factor.unit,
          });
        }} />
      )}

      {activeSegment === 'last-used' && (
        <div className="max-h-64 overflow-y-auto rounded-md border bg-muted/20 p-2">
          {lastUsedFactors.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No recently used factors. They will appear here after your first entries.
            </p>
          ) : (
            <div className="space-y-0.5">
              {lastUsedFactors.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => { onSelectLastUsed(item); }}
                  className="flex w-full items-center justify-between rounded px-3 py-2 text-left transition-colors hover:bg-muted/50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{item.description}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {item.activityName} → {item.factorName}
                    </p>
                  </div>
                  <div className="ml-3 flex flex-col items-end gap-0.5">
                    <span className="whitespace-nowrap font-mono text-xs">
                      {item.co2ePerUnit > 0
                        ? `${item.co2ePerUnit.toLocaleString('en', { maximumFractionDigits: 4 })} `
                        : '— '}
                      <span className="text-muted-foreground">{item.co2eUnit}</span>
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      used {String(item.timesUsed)}x
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {activeSegment === 'ai-assignment' && (
        <div className="rounded-md border bg-muted/20 p-3">
          {aiAssignmentContent}
        </div>
      )}

      {activeSegment === 'manual-search' && (
        <div className="rounded-md border bg-background p-3">
          {manualSearchContent}
        </div>
      )}
    </div>
  );
}

/** Searchable panel for Envirly predefined factors from the registry */
function PredefinedFactorPanel({ scope, category, unitType, onSelectFactor }: { scope?: number; category?: string; unitType?: string; onSelectFactor: (factor: PredefinedFactorEntry) => void }) {
  const { t } = useTranslation('ghg');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching } = usePredefinedFactors(
    { scope, category, unitType, query: query || undefined, page, pageSize: 20 },
  );

  const factors = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));

  return (
    <div className="space-y-2 rounded-md border bg-muted/20 p-2">
      <div className="flex items-center gap-2">
        <Search className="h-3.5 w-3.5 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          placeholder={t('factorSegmentedSelector.searchPredefinedFactors')}
          className="h-7 text-xs"
        />
      </div>

      <div className="max-h-72 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : factors.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            No predefined factors found.
          </p>
        ) : (
          <div className="space-y-0.5">
            {factors.map((factor) => (
              <button
                key={factor.id}
                type="button"
                onClick={() => { onSelectFactor(factor); }}
                className="flex w-full items-center justify-between rounded px-3 py-2 text-left transition-colors hover:bg-muted/50"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{factor.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {factor.climatiqSource ?? 'DEFRA'} · {factor.category} · {factor.region} · {factor.unit}
                  </p>
                </div>
                {factor.referenceValue && (
                  <span className="ml-3 whitespace-nowrap font-mono text-xs">
                    {factor.referenceValue}{' '}
                    <span className="text-muted-foreground">kg/{factor.unit}</span>
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <span className="text-[10px] text-muted-foreground">
            Page {String(page)} of {String(totalPages)}
          </span>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" disabled={page <= 1 || isFetching} onClick={() => { setPage((p) => p - 1); }}>
              Prev
            </Button>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" disabled={page >= totalPages || isFetching} onClick={() => { setPage((p) => p + 1); }}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
